import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F5F2EA]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#EBE9E1] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/">
            <Button variant="ghost" className="gap-2 text-[#0F5156]">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-[#0F5156] mb-2" style={{ fontFamily: 'Outfit' }}>
          Privacy Policy
        </h1>
        <p className="text-slate-500 mb-8">Last updated: February 2025</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              Strategic Architect ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium text-[#1E293B] mb-3">Information you provide:</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Account information (name, email address, password)</li>
              <li>Profile information</li>
              <li>Goals, tasks, and planning data you create</li>
              <li>Responses to daily prompts and reflections</li>
              <li>Communications with our AI coach</li>
              <li>Payment information (processed securely by Stripe)</li>
            </ul>
            <h3 className="text-xl font-medium text-[#1E293B] mb-3">Information collected automatically:</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Device information (browser type, operating system)</li>
              <li>Usage data (features used, time spent)</li>
              <li>Log data (IP address, access times)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide and maintain the Service</li>
              <li>Process your subscription and payments</li>
              <li>Personalize your experience</li>
              <li>Generate AI-powered insights and recommendations</li>
              <li>Send important updates and notifications</li>
              <li>Improve our Service</li>
              <li>Respond to your requests and support inquiries</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">4. Data Sharing</h2>
            <p className="text-slate-600 mb-4">
              We do not sell your personal data. We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Service providers:</strong> Third parties that help us operate the Service (e.g., Stripe for payments, cloud hosting providers)</li>
              <li><strong>AI providers:</strong> To generate personalized recommendations (data is anonymized where possible)</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Team members:</strong> If you join a team, relevant data is shared with team administrators</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate security measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Encryption of data in transit (SSL/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Limited access to personal data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">6. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your data for as long as your account is active or as needed to provide the Service. You can request deletion of your account and associated data at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">7. Your Rights</h2>
            <p className="text-slate-600 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">8. Cookies</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar technologies to improve your experience, analyze usage, and personalize content. You can control cookie settings through your browser.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">9. Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              The Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">10. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this privacy policy from time to time. We will notify you of significant changes via email or through the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#1E293B] mb-4">11. Contact Us</h2>
            <p className="text-slate-600">
              If you have questions about this privacy policy or our data practices, please contact us at strategicarchitect@blueprintlifestrategies.com.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#EBE9E1] bg-white/50 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Strategic Architect. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
